import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { BrandMarkLink } from "../components/BrandMarkLink";
import { PaperGrain } from "../components/PaperGrain";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { postAuthRedirect } from "../lib/roleHome";

const ERR_COPY = {
  code_invalid: "That code didn't match. Please try again.",
  code_expired: "That code has expired. Request a new one.",
  code_not_found: "We couldn't find that code. It may have already been used.",
  too_many_attempts: "Too many attempts. Please request a new code.",
  invalid_type: "Something went wrong with the link. Please request a new one.",
};

const friendly = (err) => {
  const code = err?.response?.data?.detail?.error;
  if (code && ERR_COPY[code]) return ERR_COPY[code];
  return "We couldn't verify that code. Try again or request a new one.";
};

const SUCCESS_REDIRECT = (user) => postAuthRedirect(user);

/* -------------------------------------------------------------------------- */
/*  Email magic-link flow                                                     */
/* -------------------------------------------------------------------------- */

const EmailVerify = ({ token, id, identifier }) => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | error
  const [errorMsg, setErrorMsg] = useState(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return; // StrictMode guard
    ranRef.current = true;

    (async () => {
      if (!token || !id || !identifier) {
        setStatus("error");
        setErrorMsg("This link is incomplete. Please request a new one.");
        return;
      }
      try {
        const { data } = await api.post("/auth/verify-code", {
          identifier,
          code: token,
          type: "email",
          auth_code_id: id,
        });
        login(data.token, data.user);
        navigate(SUCCESS_REDIRECT(data.user), { replace: true });
      } catch (e) {
        setStatus("error");
        setErrorMsg(friendly(e));
      }
    })();
  }, [token, id, identifier, login, navigate]);

  if (status === "loading") {
    return (
      <div data-testid="verify-email-loading" className="flex flex-col items-center text-center">
        <Loader2 size={28} className="animate-spin text-napoli-oxblood" />
        <p className="mt-4 font-body italic text-napoli-espresso-soft text-[14px]">
          Signing you in…
        </p>
      </div>
    );
  }

  return (
    <div data-testid="verify-email-error" className="text-center">
      <p className="font-display font-bold text-[26px] text-napoli-espresso">
        We couldn&rsquo;t sign you in.
      </p>
      <p className="mt-4 font-body text-[14px] text-napoli-espresso-soft">{errorMsg}</p>
      <Link
        to="/login"
        data-testid="verify-email-retry"
        className="mt-8 inline-block font-body text-[13px] tracking-widest uppercase text-napoli-oxblood border-b border-napoli-oxblood pb-1 hover:text-napoli-oxblood-dark"
      >
        Request a new link →
      </Link>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*  Phone OTP flow                                                            */
/* -------------------------------------------------------------------------- */

const OTP_LEN = 6;

const PhoneVerify = ({ identifier }) => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [digits, setDigits] = useState(Array(OTP_LEN).fill(""));
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const setAt = (i, v) => {
    const next = [...digits];
    next[i] = v;
    setDigits(next);
  };

  const onChange = (i, e) => {
    const v = e.target.value.replace(/\D/g, "").slice(-1);
    setAt(i, v);
    if (v && i < OTP_LEN - 1) inputRefs.current[i + 1]?.focus();
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
  };

  const onPaste = (e) => {
    const text = (e.clipboardData?.getData("text") || "").replace(/\D/g, "");
    if (text.length >= OTP_LEN) {
      e.preventDefault();
      const arr = text.slice(0, OTP_LEN).split("");
      setDigits(arr);
      inputRefs.current[OTP_LEN - 1]?.focus();
    }
  };

  const code = digits.join("");
  const ready = code.length === OTP_LEN;

  const submit = async (e) => {
    e?.preventDefault();
    if (!ready || submitting) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const { data } = await api.post("/auth/verify-code", {
        identifier,
        code,
        type: "phone",
      });
      login(data.token, data.user);
      navigate(SUCCESS_REDIRECT(data.user), { replace: true });
    } catch (e2) {
      setErrorMsg(friendly(e2));
      setDigits(Array(OTP_LEN).fill(""));
      inputRefs.current[0]?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  const masked = identifier
    ? identifier.replace(/(\+\d{1,3})(\d+)(\d{4})/, (_, a, _b, c) => `${a}-***-${c}`)
    : "your phone";

  return (
    <form
      onSubmit={submit}
      data-testid="verify-phone-form"
      className="flex flex-col items-center text-center"
    >
      <p className="font-body font-semibold text-[11px] tracking-[0.3em] uppercase text-napoli-oxblood">
        Verifica
      </p>
      <h1 className="mt-4 font-display font-bold text-[32px] text-napoli-espresso">
        Enter your code
      </h1>
      <p className="mt-3 font-body text-[14px] text-napoli-espresso-soft max-w-sm">
        We sent a 6-digit code to {masked}.
      </p>

      <div className="mt-8 flex gap-2 sm:gap-3" onPaste={onPaste}>
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => (inputRefs.current[i] = el)}
            data-testid={`otp-input-${i}`}
            value={d}
            onChange={(ev) => onChange(i, ev)}
            onKeyDown={(ev) => onKeyDown(i, ev)}
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            maxLength={1}
            aria-label={`Digit ${i + 1}`}
            className="w-12 h-16 sm:w-14 sm:h-16 text-center bg-napoli-cream-warm border border-napoli-gold/40 text-napoli-oxblood font-display font-semibold text-[28px] focus:outline-none focus:border-napoli-oxblood"
          />
        ))}
      </div>

      {errorMsg ? (
        <p
          data-testid="verify-phone-error"
          className="mt-5 font-body text-[13px] text-napoli-oxblood-dark"
        >
          {errorMsg}
        </p>
      ) : null}

      <button
        type="submit"
        data-testid="verify-phone-submit"
        disabled={!ready || submitting}
        className="mt-8 w-full max-w-xs bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[13px] tracking-widest uppercase py-4 hover:bg-napoli-oxblood-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
      >
        {submitting ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Verifying…
          </>
        ) : (
          "Verify"
        )}
      </button>

      <Link
        to="/login"
        data-testid="verify-phone-different-number"
        className="mt-6 font-body text-[13px] text-napoli-espresso-soft underline underline-offset-4 hover:text-napoli-oxblood"
      >
        Use a different number
      </Link>
    </form>
  );
};

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

const VerifyPage = () => {
  const [params] = useSearchParams();
  const type = params.get("type");
  const token = params.get("token");
  const id = params.get("id");
  const identifier = params.get("identifier");

  return (
    <main
      data-testid="verify-page"
      className="relative min-h-screen bg-napoli-cream text-napoli-espresso overflow-hidden flex flex-col items-center px-6 pt-16 pb-24"
    >
      <PaperGrain />
      <div className="relative z-10 w-full max-w-[480px] flex flex-col items-center">
        <BrandMarkLink size="md" theme="light" testId="verify-brand-link" ariaLabel="Napoli — home" />
        <div className="mt-12 w-full">
          {type === "phone" ? (
            <PhoneVerify identifier={identifier} />
          ) : type === "email" ? (
            <EmailVerify token={token} id={id} identifier={identifier} />
          ) : (
            <div className="text-center">
              <p className="font-display font-bold text-[26px] text-napoli-espresso">
                Nothing to verify here.
              </p>
              <Link
                to="/login"
                className="mt-6 inline-block font-body text-[13px] tracking-widest uppercase text-napoli-oxblood border-b border-napoli-oxblood pb-1"
              >
                Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default VerifyPage;
